import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { RouterLinkActive, RouterLink, Router, NavigationEnd } from '@angular/router';

import {
  ACCOUNTS_ACCESS_ROUTES,
  ADDITIONAL_FEES_ROUTES,
  CONVERSION_TOOL_ROUTES,
  CURRENCY_RATES_ROUTES,
  FORECASTED_PROFILES_ROUTES,
  HISTORICAL_PROFILE_ROUTES,
  HOURLY_COST_ROUTES,
  HOURLY_PRICE_FORECAST_ROUTES,
  MENU_ROUTES,
  PORTFOLIO_MANAGEMENT_ROUTES,
  PPA_ROUTES,
  PRICING_ROUTES,
  PRICING_TEMPLATES_ROUTES,
  SYNTHETIC_PROFILE_ROUTES,
  TARIFF_SCHEDULE_ROUTES
} from 'src/app/app.routes';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from 'src/app/core/services/auth.service';
import { filter, map, startWith, Subscription } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import type { TranslationKey } from 'src/app/core/models/translation.model';

const EXTEND_SIDE_MENU_TOKEN = 'EXTEND_SIDE_MENU_TOKEN';

@Component({
  selector: 'emp-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  imports: [RouterLinkActive, RouterLink, TranslatePipe, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SideMenuComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  public isExtended = signal(true);
  private router = inject(Router);
  public openedItems = signal<Set<string>>(new Set());

  private subs: Subscription[] = [];
  private set sub(subscription: Subscription) {
    this.subs.push(subscription);
  }

  ngOnInit(): void {
    this.initializeExtendedState();
    this.observeRouterEvents();
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  private initializeExtendedState(): void {
    const storedState = localStorage.getItem(EXTEND_SIDE_MENU_TOKEN);
    if (storedState === 'false') {
      this.isExtended.set(false);
    }
  }

  private observeRouterEvents(): void {
    this.sub = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(event => event.urlAfterRedirects),
        startWith(this.router.url)
      )
      .subscribe(url => {
        const activeItem = this.menu.find(item => url.includes(item.path));
        if (activeItem) {
          this.openedItems.update(items => new Set(items).add(activeItem.path));
        }
      });
  }

  public toggleIsOpen(path: MenuItem['path']): void {
    this.openedItems.update(items => {
      const next = new Set(items);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }

  public toggleExtended(): void {
    if (this.isExtended()) {
      localStorage.setItem(EXTEND_SIDE_MENU_TOKEN, 'false');
      this.openedItems.set(new Set());
      this.isExtended.set(false);
    } else {
      localStorage.setItem(EXTEND_SIDE_MENU_TOKEN, 'true');
      this.isExtended.set(true);
    }
  }

  public menu = [
    {
      path: MENU_ROUTES.myDashboard,
      label: 'MENU__MY_DASHBOARD',
      icon: 'grid_view',
      submodules: []
    },
    {
      path: MENU_ROUTES.meteringData,
      label: 'MENU__METERING_DATA',
      icon: 'chart_data',
      submodules: [
        {
          path: SYNTHETIC_PROFILE_ROUTES.syntheticProfiles,
          label: 'SYNTHETIC_PROFILES'
        },
        {
          path: HISTORICAL_PROFILE_ROUTES.historicalProfiles,
          label: 'HISTORICAL_PROFILES'
        },
        {
          path: FORECASTED_PROFILES_ROUTES.forecastedProfiles,
          label: 'FORECASTED_PROFILES'
        }
      ]
    },
    {
      path: MENU_ROUTES.dataProcessing,
      label: 'MENU__DATA_PROCESSING',
      icon: 'dashboard_2_gear',
      submodules: [
        {
          path: TARIFF_SCHEDULE_ROUTES.tariffSchedule,
          label: 'MENU__TARIFF_SCHEDULE'
        },
        {
          path: CONVERSION_TOOL_ROUTES.conversionTool,
          label: 'MENU__CONVERSION_TOOL'
        },
        {
          path: CURRENCY_RATES_ROUTES.currencyRates,
          label: 'MENU__CURRENCY_RATES'
        }
      ]
    },
    {
      path: MENU_ROUTES.pricing,
      label: 'MENU__PRICING',
      icon: 'shoppingmode',
      submodules: [
        {
          path: PRICING_ROUTES.allOffers,
          label: 'MENU__ALL_OFFERS'
        },
        {
          path: ADDITIONAL_FEES_ROUTES.additionalFees,
          label: 'MENU__OFFERS__ADDITIONAL_FEES'
        },
        {
          path: PRICING_TEMPLATES_ROUTES.listTemplates,
          label: 'MENU__OFFERS__PRICING_TEMPLATES'
        }
      ]
    },
    {
      path: MENU_ROUTES.priceCalculation,
      label: 'MENU__PRICE_CALCULATION',
      icon: 'function',
      submodules: [
        {
          path: HOURLY_COST_ROUTES.hourlyCost,
          label: 'MENU__HOURLY_COST'
        },
        {
          path: HOURLY_PRICE_FORECAST_ROUTES.hourlyPriceForecast,
          label: 'MENU__HOURLY_PRICE_FORECAST'
        },
        {
          path: PPA_ROUTES.ppa,
          label: 'MENU__PPA'
        }
      ]
    },
    {
      path: MENU_ROUTES.nonCommodity,
      label: 'MENU__NON_COMMODITY',
      icon: 'leaderboard',
      submodules: []
    },
    {
      path: MENU_ROUTES.scenarioBuilder,
      label: 'MENU__SCENARIO_BUILDER',
      icon: 'add_chart',
      submodules: []
    },
    {
      path: MENU_ROUTES.portfolioManagement,
      label: 'MENU__PORTFOLIO_MANAGEMENT',
      icon: 'location_away',
      submodules: [
        { path: PORTFOLIO_MANAGEMENT_ROUTES.sites, label: 'MENU__ACCOUNT_ACCESS__SITES' },
        { path: PORTFOLIO_MANAGEMENT_ROUTES.pod, label: 'MENU__ACCOUNT_ACCESS__METERING_POINTS' },
        { path: PORTFOLIO_MANAGEMENT_ROUTES.contracts, label: 'MENU__ACCOUNT_ACCESS__CONTRACTS' }
      ]
    },
    {
      path: MENU_ROUTES.accountAccess,
      label: 'MENU__ACCOUNT_ACCESS',
      icon: 'manage_accounts',
      submodules: [
        { path: ACCOUNTS_ACCESS_ROUTES.companies, label: 'MENU__ACCOUNT_ACCESS__COMPANIES' },
        { path: ACCOUNTS_ACCESS_ROUTES.groups, label: 'MENU__ACCOUNT_ACCESS__GROUPS' },
        { path: ACCOUNTS_ACCESS_ROUTES.users, label: 'MENU__ACCOUNT_ACCESS__USERS' }
      ]
    }
  ] as const satisfies MenuItem[];

  public dataLakeUrl = `https://datalake.energymarketprice.com?token=${encodeURIComponent(this.authService.authData.token)}`;
}

type MenuItem = {
  label: TranslationKey;
  path: (typeof MENU_ROUTES)[keyof typeof MENU_ROUTES];
  icon: string;
  submodules: {
    path: string;
    label: TranslationKey;
  }[];
};
